import { Dispatch, FC, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { isUserPreferencesResolved } from '../../lib/store/app/helpers';
import { AppCall } from '../../lib/store/app/types';
import { setHasSeenWelcomePopup } from '../../lib/store/common-actions';
import { hasAnyAccountDemoPlan, hasAnyAccountPaidPlan } from '../../lib/store/crm/helpers';
import { PaymentCall } from '../../lib/store/payment/types';
import { isDemoAccountActivating, isDemoAccountReference } from '../../lib/util/account-helpers';
import {
  isCallStatusError,
  isCallStatusPending,
} from '../../lib/util/error-handling/StatusByCallHelpers';
import { isPlanPurchaseCallbackInProgress } from '../../lib/util/url-helpers';

import ExperienceIllustrationEffects from '../../assets/img/stock_logos2.png';
import { useElementVisibilityById, useIsMobile, useSolanaPendingPlanPayment } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../util/routes';
import DemoAccountActivationPopup from '../DemoAccountActivationPopup/DemoAccountActivationPopup';
import { BaseModal, MobileDrawer } from '../index';

import styles from './WelcomeSubscriptionPopup.module.scss';

type OwnProps = {};

type Props = OwnProps & ReduxProps;

const WelcomeSubscriptionPopup: FC<Props> = (props: Props): JSX.Element => {
  const {
    hasSeenWelcomePopup,
    isUserPreferencesPending,
    arePreferencesResolved,
    hasPaidPlan,
    hasDemoPlan,
    hasPendingTradingAccount,
    isPendingAccountDemo,
    isSolanaPaymentPending,
    hasSolanaPaymentsError,
    markWelcomePopupSeen,
  } = props;
  const { isMobile } = useIsMobile();
  const [isDisplayDemoActivationModal, setIsDisplayDemoActivationModal] = useState(false);
  const navigate = useNavigate();
  const isAwaitingPaymentNotification = useElementVisibilityById('awaitingPaymentNotification');
  const isPlanCallback = isPlanPurchaseCallbackInProgress();
  const isAccountBeingCreated = isDemoAccountActivating() || hasPendingTradingAccount;
  const hasPaidPlanOrPending = hasPaidPlan || (hasPendingTradingAccount && !isPendingAccountDemo);
  const hasDemoPlanOrPending = hasDemoPlan || isPendingAccountDemo;
  const isSolanaPendingPlanPayment = useSolanaPendingPlanPayment();
  const areSolanaPaymentsUnresolved = isSolanaPaymentPending === 'initial' && !hasSolanaPaymentsError;
  const isOpen = arePreferencesResolved
    && !hasSeenWelcomePopup
    && !isUserPreferencesPending
    && !isDisplayDemoActivationModal
    && !isAwaitingPaymentNotification
    && !isPlanCallback
    && !isSolanaPendingPlanPayment
    && !areSolanaPaymentsUnresolved;

  const handleTryDemo = (): void => {
    closeWelcomeModal();
    setIsDisplayDemoActivationModal(true);
  };

  const closeWelcomeModal = (): void => {
    markWelcomePopupSeen();
  };

  const renderModalDescriptionText = () => {
    if (hasPaidPlanOrPending) {
      return lang.commonEnrollmentWelcomeMessageContentYourJourney();
    }
    if (hasDemoPlanOrPending) {
      return lang.commonWelcomeToPlatformDescription2();
    }

    return lang.commonWelcomeToPlatformDescription();
  };

  const navigateToProducts = (): void => {
    closeWelcomeModal();
    navigate(UI_ROUTES.products);
  };

  const renderContent = () => (
    <>
      <div className={styles.content}>
        <img src={ExperienceIllustrationEffects} className={styles.image} alt="" />
        <div className={styles.title}>
          {hasPaidPlanOrPending
            ? lang.commonEnrollmentWelcomeMessageThankYou()
            : lang.commonWelcomeToPlatform()}
        </div>
        <div className={styles.text}>{renderModalDescriptionText()}</div>
      </div>
      <div className={styles.buttonsWrapper}>
        {hasPaidPlanOrPending ? (
          <Button variant={ButtonTypesEnum.Primary} onClick={closeWelcomeModal} biggerFontSize>
            {lang.commonContinueButton()}
          </Button>
        ) : (
          <Button variant={ButtonTypesEnum.Primary} onClick={navigateToProducts} biggerFontSize>
            {lang.commonSelectPlan()}
          </Button>
        )}
        {!hasDemoPlanOrPending && !hasPaidPlanOrPending && !isAccountBeingCreated && (
          <Button variant={ButtonTypesEnum.Secondary} onClick={handleTryDemo} biggerFontSize>
            {lang.tryADemo()}
          </Button>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobileDrawer
          title=""
          onClose={closeWelcomeModal}
          isOpen={isOpen}
          showCloseButton={false}
          showTitle={false}
        >
          {renderContent()}
        </MobileDrawer>
        <DemoAccountActivationPopup
          isDisplayModal={isDisplayDemoActivationModal}
          setIsDisplayModal={setIsDisplayDemoActivationModal}
        />
      </>
    );
  }

  return (
    <>
      <BaseModal
        className={styles.modal}
        isOpen={isOpen}
        toggle={closeWelcomeModal}
      >
        {renderContent()}
      </BaseModal>
      <DemoAccountActivationPopup
        isDisplayModal={isDisplayDemoActivationModal}
        setIsDisplayModal={setIsDisplayDemoActivationModal}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasSeenWelcomePopup: state.app.userPreferencesData.hasSeenWelcomePopup,
  isUserPreferencesPending:
    isCallStatusPending(AppCall.uploadUserPreferencesData, state.app.statusByCall)
    || isCallStatusPending(AppCall.getUserPreferencesData, state.app.statusByCall),
  arePreferencesResolved: isUserPreferencesResolved(state.app),
  hasPaidPlan: hasAnyAccountPaidPlan(state.crm),
  hasDemoPlan: hasAnyAccountDemoPlan(state.crm),
  hasPendingTradingAccount: !!state.crm.pendingTradingAccountReference,
  isPendingAccountDemo: isDemoAccountReference(state.crm.pendingTradingAccountReference),
  isSolanaPaymentPending: state.payment.isSolanaPaymentPending,
  hasSolanaPaymentsError: isCallStatusError(PaymentCall.getSolanaPayments, state.payment.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  markWelcomePopupSeen: () => dispatch(setHasSeenWelcomePopup()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(WelcomeSubscriptionPopup);
